// WHAT THE REVIEWER HAS TICKED.
//
// Owner, 2026-09-28: organise the review by category, and let him tick
// categories and their children.
//
// THE PAGING DECIDES THE MODEL. A proposal is 35,503 rows on SpaceX BA03 and
// the table shows ten at a time from the server. So a category tick CANNOT
// mean "the rows currently on screen" - that would silently select a tenth of
// what it appears to, and the reviewer would apply a decision to far fewer
// rows than he ticked. A category tick means THE CATEGORY: every row in it,
// loaded or not. Row ticks refine within what is loaded.
//
// Two sets, deliberately not merged:
//   categories  names - each means EVERY row in that category
//   rows        observation keys - individual rows the reviewer picked
// A row inside a ticked category is already covered; ticking it as well
// changes nothing, which is why `covers` checks the category first.
export const NO_CATEGORY_KEY = '__none__';

export const emptySelection = () => ({ categories: new Set(), rows: new Set() });

export function toggleCategory(sel, category) {
  const categories = new Set(sel.categories);
  const key = category === '' ? NO_CATEGORY_KEY : category;
  if (categories.has(key)) categories.delete(key);
  else categories.add(key);
  return { ...sel, categories };
}

export function toggleRow(sel, observationKey) {
  const rows = new Set(sel.rows);
  if (rows.has(observationKey)) rows.delete(observationKey);
  else rows.add(observationKey);
  return { ...sel, rows };
}

export function categoryChecked(sel, category) {
  return sel.categories.has(category === '' ? NO_CATEGORY_KEY : category);
}

// A category shows as PARTIAL when the reviewer has ticked some of its rows
// individually but not the category itself. Without this a reviewer who ticks
// three rows sees an empty category box and cannot tell his ticks registered.
export function categoryPartial(sel, category, loadedRowsOfCategory) {
  if (categoryChecked(sel, category)) return false;
  const rows = loadedRowsOfCategory || [];
  return rows.some((k) => sel.rows.has(k));
}

// Is this row included in what would be applied?
export function covers(sel, category, observationKey) {
  if (categoryChecked(sel, category)) return true;
  return sel.rows.has(observationKey);
}

// What the reviewer is about to act on, in words rather than a number he has
// to interpret. Category counts come from the server over the WHOLE proposal,
// so this is honest at 35,000 rows.
export function selectionSummary(sel, categoryCounts) {
  const counts = new Map((categoryCounts || []).map((c) => [c.category === '' ? NO_CATEGORY_KEY : c.category, c.n || 0]));
  let rowsFromCategories = 0;
  for (const c of sel.categories) rowsFromCategories += counts.get(c) || 0;
  // Individually ticked rows that are NOT already inside a ticked category.
  const extra = sel.rows.size;
  return {
    categories: sel.categories.size,
    rowsFromCategories,
    looseRows: extra,
    // Deliberately not added together: a loose row may sit inside a ticked
    // category, and claiming a total we cannot verify from here would be a
    // number that looks precise and is not.
    exact: sel.categories.size === 0,
    total: sel.categories.size === 0 ? extra : null,
  };
}

export function isEmpty(sel) {
  return sel.categories.size === 0 && sel.rows.size === 0;
}
